import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/* ─── Assets ──────────────────────────────────────────────────────── */
const LOGO   = "https://assets.lolcdn.xyz/floe/gofloe_logo.png";
const MASCOT = "https://assets.lolcdn.xyz/floe/Floe_wave_hi.png";

/* ─── Inject font + keyframes once ───────────────────────────────── */
const STYLE_ID = "gofloe-banner1-styles";
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap";
  document.head.appendChild(link);

  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes gf-pulse {
      0%,100% { opacity:1; }
      50%      { opacity:0.35; }
    }
    @keyframes gf-float {
      0%,100% { transform:translateY(0); }
      50%      { transform:translateY(-7px); }
    }
    @keyframes gf-bob {
      0%,100% { transform:translateY(0) rotate(-2deg); }
      50%      { transform:translateY(-8px) rotate(2deg); }
    }
    @keyframes gf-grow {
      from { width:0; }
    }
  `;
  document.head.appendChild(style);
}

/* ─── Breakpoint hook ─────────────────────────────────────────────── */
function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

/* ─── Shared tokens ───────────────────────────────────────────────── */
const FONT = "'Plus Jakarta Sans',-apple-system,'Helvetica Neue',Arial,sans-serif";
const BG   = "linear-gradient(135deg,#f0faf8 0%,#e6f7f4 60%,#d4f0ea 100%)";

/* ─── Sub-components ─────────────────────────────────────────────── */
function Avatar({ initials, bg }) {
  return (
    <div style={{
      width:22, height:22, borderRadius:"50%", background:bg,
      display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:9, fontWeight:800, color:"white", flexShrink:0,
    }}>
      {initials}
    </div>
  );
}

function TaskCard({ style: extra = {}, index = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      style={{
        background:"#fff", border:"1px solid rgba(0,51,51,0.09)",
        borderRadius:14, padding:"14px 16px",
        boxShadow:"0 4px 20px rgba(0,51,51,0.08)", ...extra,
      }}
    >
      <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"#94a3b8", marginBottom:6 }}>Active Task</div>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <Avatar initials="IN" bg="#003333" />
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>Brand Redesign</div>
          <div style={{ fontSize:10, color:"#94a3b8", marginTop:1 }}>Installa Inc. · Design</div>
        </div>
      </div>
      <div style={{ height:5, background:"#f0f9f8", borderRadius:3, overflow:"hidden", marginTop:8 }}>
        <div style={{ height:"100%", background:"#003333", borderRadius:3, width:"74%", animation:"gf-grow 2s ease-out forwards" }} />
      </div>
      <span style={{ display:"inline-block", fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:20, marginTop:6, background:"#f0fdf4", color:"#166534" }}>
        In Progress · 74%
      </span>
    </motion.div>
  );
}

function InvoiceCard({ style: extra = {}, index = 1 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      style={{
        background:"#fff", border:"1px solid rgba(0,51,51,0.09)",
        borderRadius:14, padding:"14px 16px",
        boxShadow:"0 4px 20px rgba(0,51,51,0.08)", ...extra,
      }}
    >
      <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"#94a3b8", marginBottom:6 }}>Latest Invoice</div>
      <div style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>INV-0042</div>
      <div style={{ fontSize:10, color:"#94a3b8", marginTop:1 }}>Berri.js PVT LTD</div>
      <div style={{ fontSize:18, fontWeight:900, color:"#003333", marginTop:4 }}>$4,200</div>
      <span style={{ display:"inline-block", fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:20, marginTop:6, background:"#f0fdf4", color:"#166534" }}>
        ✓ Paid via Stripe
      </span>
    </motion.div>
  );
}

function TeamCard({ style: extra = {}, index = 2 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const members = [
    { initials:"RY", bg:"#003333" },
    { initials:"SU", bg:"#2d8cff" },
    { initials:"JA", bg:"#059669" },
    { initials:"SA", bg:"#7c3aed" },
  ];
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      style={{
        background:"#fff", border:"1px solid rgba(0,51,51,0.09)",
        borderRadius:14, padding:"14px 16px",
        boxShadow:"0 4px 20px rgba(0,51,51,0.08)", ...extra,
      }}
    >
      <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"#94a3b8", marginBottom:6 }}>Team Online</div>
      <div style={{ display:"flex", gap:6, alignItems:"center", marginTop:4 }}>
        {members.map((m) => <Avatar key={m.initials} initials={m.initials} bg={m.bg} />)}
        <span style={{ fontSize:10, color:"#94a3b8", marginLeft:4 }}>+3 more</span>
      </div>
      <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:5 }}>
        <div style={{ width:6, height:6, borderRadius:"50%", background:"#22c55e", flexShrink:0 }} />
        <span style={{ fontSize:10, color:"#64748b", fontWeight:500 }}>Live call in progress</span>
      </div>
    </motion.div>
  );
}

/* ─── Desktop layout ──────────────────────────────────────────────── */
function DesktopBanner({ ctaRef, onCtaEnter, onCtaLeave }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      style={{
        width:"100%", height:"auto", position:"relative", overflow:"hidden", padding: "26px 0",
        background:BG, display:"flex", alignItems:"center",
        borderRadius:20, border:"1px solid rgba(0,51,51,0.08)",
        boxShadow:"0 4px 32px rgba(0,51,51,0.07)",
        fontFamily:FONT, boxSizing:"border-box",
      }}
      data-testid="gofloe-banner-desktop"
    >
      {/* dot grid */}
      <div style={{
        position:"absolute", inset:0, pointerEvents:"none", zIndex:1,
        backgroundImage:"radial-gradient(circle,rgba(0,51,51,0.06) 1px,transparent 1px)",
        backgroundSize:"28px 28px",
      }} />

      {/* Left copy — staggered children */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        style={{ position:"relative", zIndex:2, padding:"0 48px", flexShrink:0, maxWidth:420 }}
      >
        <img src={LOGO} alt="goFloe" style={{ height:30, objectFit:"contain", marginBottom:16, display:"block" }} />
        <div style={{
          display:"inline-flex", alignItems:"center", gap:6,
          background:"rgba(0,51,51,0.07)", border:"1px solid rgba(0,51,51,0.12)",
          padding:"4px 12px", borderRadius:20,
          fontSize:10, fontWeight:700, letterSpacing:"0.06em",
          color:"#003333", marginBottom:14, textTransform:"uppercase",
        }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:"#22c55e", animation:"gf-pulse 2s ease-in-out infinite", flexShrink:0 }} />
          Now in 14-day free trial
        </div>
        <h1 style={{ fontSize:32, fontWeight:900, color:"#003333", lineHeight:1.12, letterSpacing:"-0.025em", margin:"0 0 10px" }}>
          Your agency.<br />
          <em style={{ fontStyle:"normal", color:"#0d9488" }}>Fully in control.</em>
        </h1>
        <p style={{ fontSize:13, color:"#4a7a74", lineHeight:1.6, margin:"0 0 22px", maxWidth:320 }}>
          Projects, invoices, HR, time tracking &amp; client portals — all under your brand.
        </p>
        <a
          ref={ctaRef}
          href="https://gofloe.app"
          style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#003333", color:"#fff", fontSize:13, fontWeight:700, fontFamily:FONT, padding:"11px 22px", borderRadius:10, textDecoration:"none", boxShadow:"0 4px 16px rgba(0,51,51,0.22)", transition:"transform 0.15s,box-shadow 0.15s", cursor:"pointer" }}
          onMouseEnter={onCtaEnter}
          onMouseLeave={onCtaLeave}
          data-testid="gofloe-banner-cta"
        >
          Start free trial →
        </a>
      </motion.div>

      {/* Right: floating cards + mascot */}
      <div style={{ position:"absolute", right:0, top:0, bottom:0, width:"50%", display:"flex", alignItems:"center", justifyContent:"center", paddingRight:36, zIndex:2 }}>
        <div style={{ position:"relative", width:"100%", height:220 }}>
          <TaskCard    index={0} style={{ position:"absolute", right:80, top:8,   width:200, animation:"gf-float 5s ease-in-out infinite" }} />
          <InvoiceCard index={1} style={{ position:"absolute", right:0,  top:52,  width:170, animation:"gf-float 6.5s ease-in-out infinite 0.9s" }} />
          <TeamCard    index={2} style={{ position:"absolute", right:40, bottom:4, width:215, animation:"gf-float 7s ease-in-out infinite 1.6s" }} />
          <motion.img
            src={MASCOT} alt="Pengu"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            style={{
              position:"absolute", right:0, bottom:-8, zIndex:3,
              width:110, height:110, objectFit:"contain",
              animation:"gf-bob 4s ease-in-out infinite",
              filter:"drop-shadow(0 6px 16px rgba(0,51,51,0.15))",
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Mobile layout ───────────────────────────────────────────────── */
function MobileBanner({ ctaRef, onCtaEnter, onCtaLeave }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      style={{
        width:"100%", position:"relative", overflow:"hidden",
        background:BG, display:"flex", flexDirection:"column", alignItems:"center",
        borderRadius:16, border:"1px solid rgba(0,51,51,0.08)",
        boxShadow:"0 4px 24px rgba(0,51,51,0.07)",
        fontFamily:FONT, boxSizing:"border-box",
      }}
      data-testid="gofloe-banner-mobile"
    >
      {/* dot grid */}
      <div style={{
        position:"absolute", inset:0, pointerEvents:"none", zIndex:1,
        backgroundImage:"radial-gradient(circle,rgba(0,51,51,0.06) 1px,transparent 1px)",
        backgroundSize:"28px 28px",
      }} />

      {/* Top copy */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        style={{ position:"relative", zIndex:2, padding:"28px 24px 20px", width:"100%", textAlign:"center" }}
      >
        <img src={LOGO} alt="goFloe" style={{ height:26, objectFit:"contain", display:"block", margin:"0 auto 14px" }} />
        <div style={{
          display:"inline-flex", alignItems:"center", gap:6,
          background:"rgba(0,51,51,0.07)", border:"1px solid rgba(0,51,51,0.12)",
          padding:"4px 12px", borderRadius:20,
          fontSize:10, fontWeight:700, letterSpacing:"0.06em",
          color:"#003333", marginBottom:14, textTransform:"uppercase",
        }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:"#22c55e", animation:"gf-pulse 2s ease-in-out infinite", flexShrink:0 }} />
          14-day free trial
        </div>
        <h1 style={{ fontSize:26, fontWeight:900, color:"#003333", lineHeight:1.15, letterSpacing:"-0.022em", margin:"0 0 10px" }}>
          Your agency.<br />
          <em style={{ fontStyle:"normal", color:"#0d9488" }}>Fully in control.</em>
        </h1>
        <p style={{ fontSize:13, color:"#4a7a74", lineHeight:1.6, margin:"0 auto 20px", maxWidth:300 }}>
          Projects, invoices, HR, time tracking &amp; client portals — all under your brand.
        </p>
        <a
          ref={ctaRef}
          href="https://gofloe.app"
          style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#003333", color:"#fff", fontSize:13, fontWeight:700, fontFamily:FONT, padding:"12px 28px", borderRadius:10, textDecoration:"none", boxShadow:"0 4px 16px rgba(0,51,51,0.22)", transition:"transform 0.15s,box-shadow 0.15s", cursor:"pointer" }}
          onMouseEnter={onCtaEnter}
          onMouseLeave={onCtaLeave}
          data-testid="gofloe-banner-cta"
        >
          Start free trial →
        </a>
      </motion.div>

      {/* Divider */}
      <div style={{ width:"calc(100% - 48px)", height:1, background:"rgba(0,51,51,0.07)", margin:"4px 0", zIndex:2 }} />

      {/* Cards — vertical stack */}
      <div style={{ position:"relative", zIndex:2, width:"100%", display:"flex", flexDirection:"column", gap:10, padding:"16px 24px" }}>
        <TaskCard    index={0} style={{ width:"100%", animation:"gf-float 5s ease-in-out infinite" }} />
        <InvoiceCard index={1} style={{ width:"100%", animation:"gf-float 6.5s ease-in-out infinite 0.9s" }} />
        <TeamCard    index={2} style={{ width:"100%", animation:"gf-float 7s ease-in-out infinite 1.6s" }} />
      </div>

      {/* Mascot */}
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.7 }}
        transition={{ duration: 0.5, delay: 0.55 }}
        style={{ position:"relative", zIndex:2, width:"100%", display:"flex", justifyContent:"center", marginTop:-8 }}
      >
        <img src={MASCOT} alt="Pengu" style={{
          width:120, height:120, objectFit:"contain",
          animation:"gf-bob 4s ease-in-out infinite",
          filter:"drop-shadow(0 6px 16px rgba(0,51,51,0.15))",
          display:"block",
        }} />
      </motion.div>
    </motion.div>
  );
}

/* ─── Main export (named + default, matching ServicesSection pattern) */
export const GoFloeBanner = function GoFloeBanner() {
  const isMobile = useIsMobile(600);
  const ctaRef   = useRef(null);

  useEffect(() => { injectStyles(); }, []);

  function onCtaEnter() {
    if (ctaRef.current) {
      ctaRef.current.style.transform = "translateY(-1px)";
      ctaRef.current.style.boxShadow = "0 6px 24px rgba(0,51,51,0.3)";
    }
  }
  function onCtaLeave() {
    if (ctaRef.current) {
      ctaRef.current.style.transform = "";
      ctaRef.current.style.boxShadow = "0 4px 16px rgba(0,51,51,0.22)";
    }
  }

  return (
    <section
      id="gofloe-banner"
      className="py-12 md:py-16 bg-[#F8FAFC]"
      data-testid="gofloe-banner-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {isMobile
          ? <MobileBanner  ctaRef={ctaRef} onCtaEnter={onCtaEnter} onCtaLeave={onCtaLeave} />
          : <DesktopBanner ctaRef={ctaRef} onCtaEnter={onCtaEnter} onCtaLeave={onCtaLeave} />}
      </div>
    </section>
  );
};

export default GoFloeBanner;